import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import SEO from '../common/SEO';
import GoogleMaps from '../components/GoogleMaps';
import ServicesNavigationFooter from '../components/ServicesNavigationFooter';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiAward, FiHeart, FiTarget, FiUsers, FiTrendingUp, FiShield, FiStar } = FiIcons;

function About() {
  const values = [
    { icon: FiAward, title: 'Qualität', description: 'Höchste Qualitätsstandards in allen Bereichen unserer Arbeit.' },
    { icon: FiHeart, title: 'Leidenschaft', description: 'Mit Herzblut und Begeisterung für das Handwerk bei jedem Projekt.' },
    { icon: FiTarget, title: 'Präzision', description: 'Millimetergenaue Ausführung für perfekte Ergebnisse.' },
    { icon: FiUsers, title: 'Teamwork', description: 'Starkes Team mit langjähriger Erfahrung und Expertise.' }
  ];

  const achievements = [
    { icon: FiTrendingUp, title: 'Über 500 Projekte', description: 'Erfolgreich abgeschlossene Projekte in der Region' },
    { icon: FiUsers, title: 'Zufriedene Kunden', description: 'Vertrauen durch Qualität und Zuverlässigkeit' },
    { icon: FiShield, title: 'Jahrelange Erfahrung', description: 'Bewährte Expertise im Innenausbau und Sanierung' },
    { icon: FiStar, title: 'Beste Bewertungen', description: 'Ausgezeichnete Kundenbewertungen sprechen für sich' }
  ];

  const timeline = [
    { year: '2016', title: 'Anfang', description: '2016 habe ich als Trockenbauer angefangen' },
    { year: '2020', title: 'Projektleiter', description: 'Deutschlandweite Projektleitung bei einem Trockenbau Unternehmen' },
    { year: '2022', title: 'Zertifizierung Einblassdämmung', description: 'Durch steigende Nachfrage auf dem Markt, habe ich die Zertifizierung gemacht.' },
    { year: '2023', title: 'Mfh. Verwaltung', description: 'Hausverwaltung eines Mehrfamilienhauses.' },
    { year: '2025', title: 'Gründung', description: 'MK Ausbau Innenausbau & Sanierung wird gegründet um der Kundennachfrage gerecht zu werden.' }
  ];

  return (
    <div className="py-20">
      <SEO 
        title="Über uns | MK-AUSBAU Heinsberg" 
        description="Lernen Sie das Team von MK-AUSBAU kennen. Ihr Experte für Innenausbau & Sanierung in Heinsberg. Qualität, Leidenschaft & Präzision seit 2016."
        canonical="/ueber"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Über uns
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Lernen Sie das Team hinter MK-AUSBAU kennen. Erfahren Sie mehr über unsere Geschichte, Werte und was uns zu Ihrem zuverlässigen Partner im Innenausbau macht.
          </p>
        </motion.div>

        {/* Rest of the component (Geschäftsführer, Video, Werte, Erfolge, Timeline, CTA) remains unchanged */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-20"
        >
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="flex-1">
              <img 
                src="https://mx.ausbau.nrw/img/max_kelm.jpg" 
                alt="Maxim Kelm - Geschäftsführer MK-AUSBAU" 
                className="w-full max-w-md h-96 object-cover rounded-2xl shadow-2xl mx-auto"
              />
            </div>
            <div className="flex-1 space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-mk-teal-500 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiUser} className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Maxim Kelm</h2>
                  <p className="text-lg text-mk-teal-600 font-semibold">Geschäftsführer & Gründer</p>
                </div>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                Als Gründer und Geschäftsführer von MK-AUSBAU bringe ich jahrelange Erfahrung im Innenausbau mit. Meine Leidenschaft für handwerkliche Perfektion und innovative Lösungen treibt unser Unternehmen täglich an.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Von der ersten Beratung bis zur finalen Abnahme stehe ich persönlich für die Qualität unserer Arbeit ein. Unser Ziel ist es, Ihre Räume nicht nur zu renovieren, sondern sie in Wohlfühloasen zu verwandeln.
              </p>
              <div className="pt-4">
                <Link 
                  to="/kontakt"
                  className="inline-flex items-center space-x-2 bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-6 py-3 rounded-sm font-semibold transition-colors"
                >
                  <span>Persönliches Gespräch vereinbaren</span>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Video Sektion */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unser Unternehmen im Video
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Erhalten Sie einen Einblick in unsere Arbeitsweise und lernen Sie unser Team kennen.
            </p>
          </div>
          <div className="relative w-full max-w-4xl mx-auto bg-black rounded-2xl overflow-hidden shadow-2xl">
            <div className="relative pb-[56.25%] h-0">
              <iframe 
                src="https://player.vimeo.com/video/1112840703?badge=0&autopause=0&player_id=0&amp" 
                frameBorder="0" 
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write" 
                className="absolute top-0 left-0 w-full h-full" 
                title="MK-AUSBAU Unternehmensfilm"
              ></iframe>
            </div>
          </div>
        </motion.div>

        {/* Unsere Werte */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unsere Werte
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Diese Grundprinzipien leiten uns bei jedem Projekt und jeder Kundenbeziehung.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div 
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                className="text-center bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 bg-mk-teal-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Unsere Erfolge */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="bg-mk-teal-50 p-8 rounded-xl mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unsere Erfolge
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Zahlen und Fakten, die für unsere Kompetenz und Zuverlässigkeit sprechen.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div 
                key={achievement.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-mk-red-700 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={achievement.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {achievement.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {achievement.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Meine Geschichte - Mobile Optimiert */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Meine Geschichte
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Von den Anfängen bis heute - die Entwicklung von MK-AUSBAU im Überblick.
            </p>
          </div>

          {/* Mobile Timeline Layout */}
          <div className="block lg:hidden space-y-8">
            {timeline.map((item, index) => (
              <motion.div 
                key={item.year}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg"
              >
                <div className="flex items-start space-x-4 mb-3">
                  <div className="w-12 h-8 bg-mk-red-700 text-white rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {item.year}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 break-words">
                      {item.title}
                    </h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Desktop Timeline Layout */}
          <div className="hidden lg:block relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-mk-teal-200"></div>
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <motion.div 
                  key={item.year}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                  className={`relative flex items-center ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}
                >
                  <div className={`w-5/12 ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}>
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-12 h-8 bg-mk-red-700 text-white rounded-lg flex items-center justify-center text-sm font-bold">
                          {item.year}
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">
                          {item.title}
                        </h3>
                      </div>
                      <p className="text-gray-600">
                        {item.description}
                      </p>
                    </div>
                  </div>
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-mk-teal-500 rounded-full border-4 border-white shadow-lg"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center bg-gradient-to-r from-mk-teal-500 to-mk-teal-600 text-white p-12 rounded-xl"
        >
          <h2 className="text-3xl font-bold mb-4">
            Bereit für Ihr nächstes Projekt?
          </h2>
          <p className="text-xl text-mk-teal-100 mb-8 max-w-2xl mx-auto">
            Lassen Sie uns gemeinsam Ihre Vorstellungen verwirklichen. Kontaktieren Sie uns für ein persönliches Beratungsgespräch!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/kontakt"
              className="inline-flex items-center justify-center space-x-2 bg-white text-mk-teal-600 px-8 py-3 rounded-sm font-semibold hover:bg-gray-100 transition-colors"
            >
              <span>Jetzt Kontakt aufnehmen</span>
            </Link>
            <Link 
              to="/services"
              className="inline-flex items-center justify-center space-x-2 bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-3 rounded-sm font-semibold transition-colors"
            >
              <span>Unsere Leistungen entdecken</span>
            </Link>
          </div>
        </motion.div>
      </div>
      
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default About;